import { useEffect, useState } from 'react';
import { X, Loader2, RefreshCw, Copy, Check, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';

import { useCreateUser, useUpdateUser } from '@/features/users/useUsers';
import { generatePassword } from '@/lib/password';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/stores/toast.store';
import { useT, type StringKey } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import type { AdminUser, CreateUserInput, NavigatorScope, UpdateUserInput, UserRole } from '@/types/api';
import { cn } from '@/lib/utils';

interface UserModalProps {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

const ROLES: Array<{ value: UserRole; tk: StringKey }> = [
  { value: 'agent', tk: 'admin.team.edit.role.agent' },
  { value: 'supervisor', tk: 'admin.team.edit.role.supervisor' },
  { value: 'admin', tk: 'admin.team.edit.role.admin' },
  { value: 'uploader', tk: 'admin.team.edit.role.uploader' },
  { value: 'navigator', tk: 'admin.team.edit.role.navigator' },
];

const LANGUAGE_OPTIONS: Array<{ value: string; tk: StringKey }> = [
  { value: 'en', tk: 'admin.team.edit.lang.en' },
  { value: 'es', tk: 'admin.team.edit.lang.es' },
  { value: 'other', tk: 'admin.team.edit.lang.other' },
];

/** 076 (D-3): solo se pinta con rol navigator. Default full. */
const SCOPE_OPTIONS: Array<{ value: NavigatorScope; tk: StringKey }> = [
  { value: 'full', tk: 'admin.team.edit.scope.full' },
  { value: 'closer', tk: 'admin.team.edit.scope.closer' },
  { value: 'pool', tk: 'admin.team.edit.scope.pool' },
];

export function UserModal({ open, user, onClose }: UserModalProps): JSX.Element | null {
  const t = useT();
  const isEdit = !!user;
  const me = useAuthStore((s) => s.user);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>('agent');
  // E-35 (Edd, 2026-10-21, «Obligar a elegir»): al crear, '' = sin elegir.
  // El <select> es `required`, así que el formulario no sale sin tipo, y el
  // servidor además rechaza el alta sin tipo (NAVIGATOR_SCOPE_REQUIRED).
  const [navigatorScope, setNavigatorScope] = useState<NavigatorScope | ''>('');
  const [languages, setLanguages] = useState<string[]>(['en']);
  const [active, setActive] = useState(true);
  const [dailyTargetOverride, setDailyTargetOverride] = useState<number | null>(null);
  const [useSystemTarget, setUseSystemTarget] = useState(true);
  const [password, setPassword] = useState('');
  const [pwCopied, setPwCopied] = useState(false);

  const createMut = useCreateUser();
  const updateMut = useUpdateUser();

  const editingSelf = isEdit && user && me && String(user.id) === String(me.id);

  useEffect(() => {
    if (!open) return;
    if (user) {
      setEmail(user.email);
      setFullName(user.fullName);
      setRole(user.role);
      setNavigatorScope(user.navigatorScope ?? 'full');
      setLanguages(user.languages?.length ? user.languages : ['en']);
      setActive(user.active);
      setDailyTargetOverride(user.dailyTargetOverride);
      setUseSystemTarget(user.dailyTargetOverride === null);
      setPassword('');
    } else {
      setEmail('');
      setFullName('');
      setRole('agent');
      setNavigatorScope('');
      setLanguages(['en']);
      setActive(true);
      setDailyTargetOverride(null);
      setUseSystemTarget(true);
      setPassword(generatePassword());
    }
    createMut.reset();
    updateMut.reset();
    setPwCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !createMut.isPending && !updateMut.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, createMut.isPending, updateMut.isPending]);

  if (!open) return null;

  const error = (createMut.error ?? updateMut.error) as Error | null;
  const isPending = createMut.isPending || updateMut.isPending;

  const toggleLanguage = (lang: string): void => {
    setLanguages((prev) => {
      if (prev.includes(lang)) {
        const next = prev.filter((l) => l !== lang);
        return next.length === 0 ? ['en'] : next;
      }
      return [...prev, lang];
    });
  };

  const copyPassword = async (): Promise<void> => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setPwCopied(true);
      window.setTimeout(() => setPwCopied(false), 2000);
    } catch {
      toast.warning("Couldn't copy", 'Select the password manually instead.');
    }
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (isEdit && user) {
      const patch: UpdateUserInput = {};
      const trimmedEmail = email.toLowerCase().trim();
      if (trimmedEmail !== user.email.toLowerCase()) patch.email = trimmedEmail;
      if (fullName.trim() !== user.fullName) patch.fullName = fullName.trim();
      if (!editingSelf && role !== user.role) patch.role = role;
      if (role === 'navigator' && navigatorScope !== '' && navigatorScope !== (user.navigatorScope ?? 'full')) {
        patch.navigatorScope = navigatorScope;
      }
      if (JSON.stringify(languages) !== JSON.stringify(user.languages)) patch.languages = languages;
      if (!editingSelf && active !== user.active) patch.active = active;
      const newOverride = useSystemTarget ? null : dailyTargetOverride;
      if (newOverride !== user.dailyTargetOverride) patch.dailyTargetOverride = newOverride;

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      updateMut.mutate(
        { id: user.id, input: patch },
        {
          onSuccess: () => {
            toast.success(t('admin.team.edit.toastUpdated'), t('admin.team.edit.toastUpdatedDesc', { name: user.fullName }));
            onClose();
          },
        },
      );
    } else {
      const input: CreateUserInput = {
        email: email.toLowerCase().trim(),
        fullName: fullName.trim(),
        role,
        languages,
        initialPassword: password,
        // E-35: el tipo viaja SIEMPRE para un navegador (antes solo si no era
        // 'full', y el servidor completaba 'full' en silencio).
        ...(role === 'navigator' && navigatorScope !== '' ? { navigatorScope } : {}),
      };
      createMut.mutate(input, {
        onSuccess: (created) => {
          toast.success(
            'User created',
            `${created.fullName} can now sign in. Make sure they get the password.`,
          );
          onClose();
        },
      });
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={() => !isPending && onClose()}
      ariaLabelledBy="user-modal-title"
    >
      <header className="sticky top-0 bg-white border-b border-nav-card px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
        <div className="min-w-0 flex-1">
          <h2 id="user-modal-title" className="font-head text-[17px] font-bold text-nav-navy">
            {isEdit ? t('admin.team.edit.title') : 'New user'}
          </h2>
          {isEdit && (
            <p className="text-xs text-nav-slate mt-0.5 truncate">{t('admin.team.edit.subtitle')}</p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto">
        <form onSubmit={onSubmit} className="px-6 py-5 space-y-4 font-body text-nav-navy">
          {error && (
            <Alert variant="error" role="alert">
              <AlertTitle>{isEdit ? 'Update failed' : 'Create failed'}</AlertTitle>
              <AlertDescription>
                {error instanceof ApiError ? error.message : error.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Polished card: Identity (email + fullName) */}
          <div className="rounded-xl bg-white border border-nav-card shadow-sm hover:shadow transition-shadow p-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="user-email">{t('admin.team.edit.emailLabel')}</Label>
              <Input
                id="user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                maxLength={255}
                className="min-h-[44px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="user-name">{t('admin.team.edit.fullNameLabel')}</Label>
              <Input
                id="user-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                minLength={1}
                maxLength={100}
                className="min-h-[44px]"
              />
            </div>
          </div>

          {/* Polished card: Role + Languages */}
          <div className="rounded-xl bg-white border border-nav-card shadow-sm hover:shadow transition-shadow p-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="user-role" className="flex items-center gap-1.5">
                {t('admin.team.edit.roleLabel')}
                {editingSelf && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] text-nav-slate"
                    title={t('admin.team.edit.cantEditSelf')}
                  >
                    <Info className="h-3 w-3" />
                    {t('admin.team.edit.cantEditSelf')}
                  </span>
                )}
              </Label>
              <Select
                id="user-role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                disabled={!!editingSelf}
                className="min-h-[44px]"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {t(r.tk)}
                  </option>
                ))}
              </Select>
            </div>

            {role === 'navigator' && (
              <div className="space-y-1.5">
                <Label htmlFor="user-scope">{t('admin.team.edit.scopeLabel')}</Label>
                <Select
                  id="user-scope"
                  value={navigatorScope}
                  onChange={(e) => setNavigatorScope(e.target.value as NavigatorScope)}
                  required
                  className="min-h-[44px]"
                >
                  {navigatorScope === '' && (
                    <option value="" disabled>
                      {t('admin.team.edit.scopePlaceholder')}
                    </option>
                  )}
                  {SCOPE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.tk)}
                    </option>
                  ))}
                </Select>
                <p className="text-[11px] text-nav-slate">{t('admin.team.edit.scopeHint')}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('admin.team.edit.languagesLabel')}</Label>
              <div className="flex flex-wrap gap-2">
                {LANGUAGE_OPTIONS.map((l) => {
                  const on = languages.includes(l.value);
                  return (
                    <button
                      key={l.value}
                      type="button"
                      onClick={() => toggleLanguage(l.value)}
                      className={cn(
                        'inline-flex items-center min-h-[44px] px-[15px] rounded-full font-head text-[13px] font-bold border-[1.5px] transition-colors duration-150 motion-reduce:transition-none',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                        on
                          ? 'bg-nav-tealTint text-nav-tealDark border-nav-teal'
                          : 'bg-white text-nav-slate border-nav-card hover:bg-nav-soft hover:text-nav-navy'
                      )}
                      aria-pressed={on}
                    >
                      {t(l.tk)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Polished card: Daily target + Active (edit only) */}
          {isEdit && (
            <div className="rounded-xl bg-white border border-nav-card shadow-sm hover:shadow transition-shadow p-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="user-target">{t('admin.team.edit.dailyTargetLabel')}</Label>
                <p className="text-[11px] text-nav-slate">{t('admin.team.edit.dailyTargetHint')}</p>
                <div className="flex items-center gap-3 mt-1">
                  <Input
                    id="user-target"
                    type="number"
                    min={0}
                    max={500}
                    value={useSystemTarget ? '' : (dailyTargetOverride ?? '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '') {
                        setDailyTargetOverride(null);
                      } else {
                        const n = parseInt(v, 10);
                        if (Number.isFinite(n) && n >= 0) setDailyTargetOverride(n);
                      }
                    }}
                    disabled={useSystemTarget}
                    placeholder={useSystemTarget ? '—' : '0'}
                    className="w-32 min-h-[44px]"
                  />
                  <Checkbox
                    checked={useSystemTarget}
                    onChange={(e) => {
                      setUseSystemTarget(e.target.checked);
                      if (e.target.checked) setDailyTargetOverride(null);
                    }}
                    label={t('admin.team.edit.useSystemDefault')}
                  />
                </div>
              </div>

              <div className="pt-1 border-t border-nav-hair">
                <div className="flex items-start gap-3 pt-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={active}
                    aria-label={t('admin.team.edit.activeLabel')}
                    disabled={!!editingSelf}
                    onClick={() => !editingSelf && setActive((v) => !v)}
                    className={cn(
                      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                      active ? 'bg-nav-sageMid' : 'bg-nav-line',
                      editingSelf && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
                        active ? 'translate-x-5' : 'translate-x-0.5'
                      )}
                    />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-nav-navy">{t('admin.team.edit.activeLabel')}</p>
                    <p className="text-[11px] text-nav-slate mt-0.5">
                      {editingSelf ? t('admin.team.edit.cantDeactivateSelf') : t('admin.team.edit.activeHint')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Audit summary footer */}
          {isEdit && user && (
            <p className="text-[11px] text-nav-slate italic">
              {t('admin.team.edit.lastUpdated', {
                by: 'admin',
                when: user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '—',
              })}
            </p>
          )}

          {!isEdit && (
            <div className="rounded-xl bg-white border border-nav-card shadow-sm p-4 space-y-2">
              <Label htmlFor="user-pw">Initial password</Label>
              <div className="flex gap-2">
                <Input
                  id="user-pw"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="min-h-[44px]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={() => setPassword(generatePassword())}
                  aria-label="Generate password"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  onClick={copyPassword}
                  aria-label="Copy password"
                >
                  {pwCopied ? <Check className="h-4 w-4 text-nav-sageMid" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-[11px] text-nav-slate">
                Share this password with the new user out-of-band. They'll be required to change it on first sign-in.
              </p>
            </div>
          )}
        </form>
      </div>

      <footer className="sticky bottom-0 bg-white border-t border-nav-card px-6 py-4 flex justify-end gap-2 rounded-b-lg">
        <Button variant="ghost" onClick={onClose} disabled={isPending}>
          {t('admin.team.edit.cancel')}
        </Button>
        <Button onClick={onSubmit} disabled={isPending}>
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isPending ? (isEdit ? t('admin.team.edit.saving') : 'Creating…') : isEdit ? t('admin.team.edit.save') : 'Create user'}
        </Button>
      </footer>
    </ModalShell>
  );
}
